import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    type TextInputSelectionChangeEventData,
    TextInput as NativeTextInput,
    type NativeSyntheticEvent,
    type PointerEvent,
    type FocusEvent,
    type BlurEvent,
    Animated,
    Platform,
    View
} from "react-native";
import type IInputOTPProps from "./type";
import {
    type InputOTPAccessibilityProps,
    type InputOTPCaretProps,
    type InputOTPSelection,
    type InputOTPInstance,
    type IInputOTPRef
} from "./type";
import stylesheet, {
    getInputOTPCharacters,
    getInputOTPFieldType,
    getInputOTPAutofill,
    INPUT_OTP_KEYBOARDS,
    getInputOTPSizeKey,
    getInputOTPChange,
    getInputOTPColors,
    getInputOTPItems,
    INPUT_OTP_SIZES,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getMotionDuration,
    useReducedMotion
} from "../../helpers/motion";
import {
    useAnimatedState
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    type RefForwardingComponent
} from "../../types";
import type {
    FieldType
} from "../fieldControl/type";
import {
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    CircleAlert as CircleAlertIcon,
    Minus as MinusIcon,
    Info as InfoIcon,
    type LucideIcon
} from "lucide-react-native";
import {
    getFieldIDs
} from "../fieldControl/stylesheet";
import {
    uuid
} from "../../utils";
import Loading from "../loading";
import Text from "../text";

const preventSlotFocusSteal = (event: {
    preventDefault: () => void;
}) => {
    event.preventDefault();
};

const InputOTPHintIcons: Partial<Record<FieldType, LucideIcon>> = {
    warning: TriangleAlertIcon,
    success: CircleCheckIcon,
    danger: CircleAlertIcon,
    info: InfoIcon
};

const InputOTPCaret: FC<InputOTPCaretProps> = ({
    customTheme,
    style
}) => {
    const {
        durations
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const [
        isVisible,
        setIsVisible
    ] = useState(true);

    const blinkDuration = getMotionDuration({
        isReduceMotionEnabled,
        duration: "blink",
        motion: "loop",
        durations
    });

    useEffect(() => {
        setIsVisible(true);

        if(!blinkDuration) {
            return;
        }

        const timer = setInterval(() => {
            setIsVisible((previousState) => !previousState);
        }, blinkDuration / 2);

        return () => {
            clearInterval(timer);
        };
    }, [
        blinkDuration
    ]);

    return <View
        style={[
            style,
            {
                opacity: isVisible ? 1 : 0
            }
        ]}
        aria-hidden={true}
    />;
};

const InputOTP: RefForwardingComponent<IInputOTPRef, IInputOTPProps> = ({
    characterSet = "numeric",
    onFocus: onFocusProp,
    isDisabled = false,
    onBlur: onBlurProp,
    isLoading = false,
    isMasked = false,
    type = "primary",
    value: valueProp,
    customLocalize,
    initialValue,
    customTheme,
    length = 6,
    onComplete,
    groupSize,
    hintText,
    onChange,
    style,
    title,
    size,
    id,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme, customLocalize);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const inputRef = useRef<InputOTPInstance | null>(null);

    const isReplaceOnFirstInput = useRef(false);

    const generatedID = useRef(uuid());

    const [
        internalValue,
        setInternalValue
    ] = useState(() => getInputOTPCharacters({
        text: initialValue ?? "",
        characterSet
    }).slice(0, length));

    const [
        selection,
        setSelection
    ] = useState<InputOTPSelection>(() => ({
        start: internalValue.length,
        end: internalValue.length
    }));

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const [
        isLastFlashing,
        setIsLastFlashing
    ] = useState(false);

    const flashOpacity = useAnimatedState({
        onFinish: () => setIsLastFlashing(false),
        value: isLastFlashing ? 1 : 0,
        intent: "hover",
        customTheme
    });

    const currentValue = valueProp === undefined ? internalValue : getInputOTPCharacters({
        text: valueProp,
        characterSet
    }).slice(0, length);

    const currentSize = getInputOTPSizeKey({
        pointerType,
        size
    });

    const currentColors = getInputOTPColors({
        isDisabled,
        isLoading,
        colors,
        type
    });

    const fieldType = getInputOTPFieldType({
        type
    });

    const fieldIDs = getFieldIDs({
        inputID: id ?? generatedID.current
    });

    const items = getInputOTPItems({
        groupSize,
        length
    });

    const keyboardRole = INPUT_OTP_KEYBOARDS[characterSet];

    const sizeRole = INPUT_OTP_SIZES[currentSize];

    const isInteractive = !isDisabled && !isLoading;

    const isActiveVisible = isFocused && isInteractive;

    const activeIndex = Math.min(selection.start, length - 1);

    const HintIcon = InputOTPHintIcons[fieldType];

    const webDirectionProps = Platform.OS === "web" ? {
        dir: "ltr"
    } : {};

    const {
        hintTextIcon: hintTextIconDynamicStyle,
        activeSlot: activeSlotDynamicStyle,
        container: containerDynamicStyle,
        hintText: hintTextDynamicStyle,
        caret: caretDynamicStyle,
        flash: flashDynamicStyle,
        group: groupDynamicStyle,
        input: inputDynamicStyle,
        slot: slotDynamicStyle,
        dot: dotDynamicStyle
    } = useStyles({
        isFocusedSlotVisible: isActiveVisible,
        size: currentSize,
        controlHeights,
        currentColors,
        shapeConfigs,
        touchTargets,
        pointerType,
        shadowAlpha,
        elevations,
        isDisabled,
        typography,
        iconSizes,
        isHovered,
        isLoading,
        radiuses,
        borders,
        colors,
        spaces,
        type
    });

    useImperativeHandle(
        ref,
        () => ({
            clear,
            focus,
            blur
        })
    );

    useEffect(() => {
        isReplaceOnFirstInput.current = type === "danger";
    }, [
        type
    ]);

    useEffect(() => {
        if(isFocused) {
            return;
        }

        setSelection({
            start: currentValue.length,
            end: currentValue.length
        });
    }, [
        currentValue.length,
        isFocused
    ]);

    const focus = () => {
        inputRef.current?.focus();
    };

    const blur = () => {
        inputRef.current?.blur();
    };

    const applyValue = (nextValue: string, cursor: number) => {
        setSelection({
            start: cursor,
            end: cursor
        });

        if(nextValue === currentValue) {
            return;
        }

        setInternalValue(nextValue);

        if(onChange) {
            onChange(nextValue);
        }

        if(nextValue.length === length && onComplete) {
            onComplete(nextValue);
        }
    };

    const clear = () => {
        applyValue("", 0);
    };

    const onChangeText = (text: string) => {
        const change = getInputOTPChange({
            isReplaceOnFirstInput: isReplaceOnFirstInput.current,
            previousValue: currentValue,
            cursor: selection.start,
            characterSet,
            length,
            text
        });

        isReplaceOnFirstInput.current = false;

        if(currentValue.length === length && text.length >= currentValue.length) {
            setIsLastFlashing(true);
        }

        applyValue(change.value, change.cursor);
    };

    const onSelectionChange = (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
        const {
            start,
            end
        } = event.nativeEvent.selection;

        if(start === selection.start && end === selection.end) {
            return;
        }

        setSelection({
            start,
            end
        });
    };

    const onFocus = (event: FocusEvent) => {
        setIsFocused(true);

        if(onFocusProp) {
            onFocusProp(event);
        }
    };

    const onBlur = (event: BlurEvent) => {
        setIsFocused(false);

        if(onBlurProp) {
            onBlurProp(event);
        }
    };

    const onGroupPointerEnter = (event: PointerEvent) => {
        if(event.nativeEvent.pointerType === "touch") {
            return;
        }

        setIsHovered(true);
    };

    const onGroupPointerLeave = () => {
        setIsHovered(false);
    };

    const webFocusProps = Platform.OS === "web" ? {
        onMouseDown: preventSlotFocusSteal
    } : {};

    const getIsFocusResponder = () => {
        return isInteractive;
    };

    const handleSlotPress = (index: number) => {
        const cursor = Math.min(index, currentValue.length);

        setSelection({
            start: cursor,
            end: cursor
        });

        focus();
    };

    const handleFocusRequest = () => {
        if(!isInteractive) {
            return;
        }

        focus();
    };

    const getInputAccessibilityProps = () => {
        const accessibilityProps: InputOTPAccessibilityProps = {
            "aria-label": title ?? localize("verification-code"),
            "aria-invalid": type === "danger" || undefined,
            "aria-disabled": isDisabled || undefined,
            "aria-busy": isLoading || undefined
        };

        if(!hintText) {
            return accessibilityProps;
        }

        if(Platform.OS === "web") {
            accessibilityProps["aria-describedby"] = fieldIDs.hintTextID;

            return accessibilityProps;
        }

        accessibilityProps.accessibilityHint = hintText;

        return accessibilityProps;
    };

    const renderSlotContent = (index: number) => {
        const character = currentValue.charAt(index);

        if(character) {
            if(isMasked) {
                return <View
                    style={dotDynamicStyle}
                />;
            }

            return <Text
                customColor={currentColors.valueColor}
                style={stylesheet.slotText}
                variant={sizeRole.variant}
                customTheme={customTheme}
            >
                {character}
            </Text>;
        }

        if(!isActiveVisible || index !== activeIndex || index !== currentValue.length) {
            return null;
        }

        return <InputOTPCaret
            customTheme={customTheme}
            style={caretDynamicStyle}
        />;
    };

    const renderSlot = (index: number) => {
        return <View
            style={[
                stylesheet.slot,
                isDisabled ? stylesheet.disabledSlot : undefined,
                isLoading ? stylesheet.loadingSlot : undefined,
                slotDynamicStyle,
                isActiveVisible && index === activeIndex ? activeSlotDynamicStyle : undefined
            ]}
            onStartShouldSetResponder={getIsFocusResponder}
            onResponderRelease={() => {
                handleSlotPress(index);
            }}
            {...webFocusProps}
            key={`slot-${index}`}
            aria-hidden={true}
        >
            {
                index === length - 1 ?
                    <Animated.View
                        style={[
                            stylesheet.flash,
                            flashDynamicStyle,
                            {
                                opacity: flashOpacity
                            }
                        ]}
                    />
                :
                    null
            }
            {renderSlotContent(index)}
        </View>;
    };

    const renderSeparator = (index: number) => {
        return <View
            style={stylesheet.separator}
            key={`separator-${index}`}
            aria-hidden={true}
        >
            <MinusIcon
                color={currentColors.separatorColor}
                size={iconSizes.small}
            />
        </View>;
    };

    const renderLabel = () => {
        if(!title) {
            return null;
        }

        return <View
            onStartShouldSetResponder={getIsFocusResponder}
            onResponderRelease={handleFocusRequest}
            style={stylesheet.labelContainer}
            {...webFocusProps}
        >
            <Text
                customColor={currentColors.labelColor}
                customTheme={customTheme}
                variant="labelMediumSize"
                id={fieldIDs.labelID}
            >
                {title}
            </Text>
        </View>;
    };

    const renderHintIcon = () => {
        if(isLoading) {
            return <View
                style={[
                    stylesheet.hintTextIcon,
                    hintTextIconDynamicStyle
                ]}
            >
                <Loading
                    customColor={currentColors.hintTextIconColor}
                    customTheme={customTheme}
                    size={iconSizes.small}
                />
            </View>;
        }

        if(!HintIcon || !hintText) {
            return null;
        }

        return <View
            style={[
                stylesheet.hintTextIcon,
                hintTextIconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <HintIcon
                color={currentColors.hintTextIconColor}
                size={iconSizes.small}
            />
        </View>;
    };

    const renderHint = () => {
        if(!hintText && !isLoading) {
            return null;
        }

        return <View
            style={[
                stylesheet.hintText,
                hintTextDynamicStyle
            ]}
            id={fieldIDs.hintTextID}
            aria-live="polite"
        >
            {renderHintIcon()}
            {
                hintText ?
                    <Text
                        customColor={currentColors.hintTextColor}
                        style={stylesheet.hintTextContent}
                        customTheme={customTheme}
                        variant="bodyMediumSize"
                    >
                        {hintText}
                    </Text>
                :
                    null
            }
        </View>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderLabel()}
        <View
            {...webDirectionProps}
            onPointerEnter={onGroupPointerEnter}
            onPointerLeave={onGroupPointerLeave}
            style={[
                stylesheet.group,
                groupDynamicStyle
            ]}
        >
            <NativeTextInput
                {...getInputAccessibilityProps()}
                {...getInputOTPAutofill({
                    platform: Platform.OS,
                    isMasked
                })}
                keyboardType={keyboardRole.keyboardType}
                inputMode={keyboardRole.inputMode}
                {...props}
                onSelectionChange={onSelectionChange}
                underlineColorAndroid="transparent"
                selectionColor="transparent"
                onChangeText={onChangeText}
                secureTextEntry={isMasked}
                editable={!isDisabled}
                id={fieldIDs.inputID}
                selection={selection}
                autoCapitalize="none"
                style={[
                    stylesheet.input,
                    inputDynamicStyle
                ]}
                value={currentValue}
                readOnly={isLoading}
                autoCorrect={false}
                spellCheck={false}
                caretHidden={true}
                onFocus={onFocus}
                multiline={false}
                onBlur={onBlur}
                ref={inputRef}
            />
            {
                items.map((item) => {
                    if(item.kind === "separator") {
                        return renderSeparator(item.index);
                    }

                    return renderSlot(item.index);
                })
            }
        </View>
        {renderHint()}
    </View>;
};
export default forwardRef(InputOTP);
